import { AddressAutocompleteInput } from '../../../../components/common/AddressAutocompleteInput';
// pages/hr/wizard/steps/PersonalDetailsStep.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Button,
  Select,
  Option,
  Divider,
} from '@mui/joy';
import type { WizardStepProps } from '../../../../types/hr';

interface PersonalDetailsData {
  first_name: string;
  last_name: string;
  phone: string;
  date_of_birth: string;
  ssn_last_four: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  zip_code: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  emergency_contact_relationship: string;
}

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

export const PersonalDetailsStep: React.FC<WizardStepProps> = ({
  step,
  sessionState,
  onDataChange,
  onNext,
  onBack,
  isFirstStep,
}) => {
  const [formData, setFormData] = useState<PersonalDetailsData>({
    // Kept as the two fields HR entered. Splitting the joined name on the first
    // space made "Mary Jo Smith" first "Mary", last "Jo Smith".
    first_name: sessionState.team_member_first_name,
    last_name: sessionState.team_member_last_name,
    // HR was already asked for this on the invite.
    phone: sessionState.team_member_phone,
    date_of_birth: '',
    ssn_last_four: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    zip_code: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
    ...((sessionState.step_data[step.id] || {}) as Partial<PersonalDetailsData>),
  });

  const [errors, setErrors] = useState<Partial<Record<keyof PersonalDetailsData, string>>>({});

  // Update parent with form data changes
  useEffect(() => {
    onDataChange(formData as unknown as Record<string, unknown>);
  }, [formData]);

  const handleChange = (field: keyof PersonalDetailsData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when field is modified
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof PersonalDetailsData, string>> = {};

    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.date_of_birth) newErrors.date_of_birth = 'Date of birth is required';
    if (!formData.address_line_1.trim()) newErrors.address_line_1 = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state) newErrors.state = 'State is required';
    if (!formData.zip_code.trim()) newErrors.zip_code = 'ZIP code is required';
    if (!formData.emergency_contact_name.trim()) newErrors.emergency_contact_name = 'Emergency contact name is required';
    if (!formData.emergency_contact_phone.trim()) newErrors.emergency_contact_phone = 'Emergency contact phone is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onNext();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography level="h4" sx={{ mb: 1 }}>
        Personal Details
      </Typography>
      <Typography level="body-sm" color="neutral" sx={{ mb: 3 }}>
        Please provide your personal information. This will be used for your employment records.
      </Typography>

      {/* Basic Information */}
      <Typography level="title-md" sx={{ mb: 2 }}>
        Basic Information
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
        <FormControl error={!!errors.first_name}>
          <FormLabel>First Name *</FormLabel>
          <Input
            value={formData.first_name}
            onChange={(e) => handleChange('first_name', e.target.value)}
            placeholder="John"
          />
          {errors.first_name && (
            <Typography level="body-xs" color="danger">
              {errors.first_name}
            </Typography>
          )}
        </FormControl>
        <FormControl error={!!errors.last_name}>
          <FormLabel>Last Name *</FormLabel>
          <Input
            value={formData.last_name}
            onChange={(e) => handleChange('last_name', e.target.value)}
            placeholder="Doe"
          />
          {errors.last_name && (
            <Typography level="body-xs" color="danger">
              {errors.last_name}
            </Typography>
          )}
        </FormControl>
        <FormControl error={!!errors.phone}>
          <FormLabel>Phone Number *</FormLabel>
          <Input
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            placeholder="(555) 123-4567"
          />
          {errors.phone && (
            <Typography level="body-xs" color="danger">
              {errors.phone}
            </Typography>
          )}
        </FormControl>
        <FormControl error={!!errors.date_of_birth}>
          <FormLabel>Date of Birth *</FormLabel>
          <Input
            type="date"
            value={formData.date_of_birth}
            onChange={(e) => handleChange('date_of_birth', e.target.value)}
          />
          {errors.date_of_birth && (
            <Typography level="body-xs" color="danger">
              {errors.date_of_birth}
            </Typography>
          )}
        </FormControl>
      </Box>

      <FormControl sx={{ mb: 3, maxWidth: 200 }}>
        <FormLabel>Last 4 of SSN</FormLabel>
        <Input
          value={formData.ssn_last_four}
          onChange={(e) => handleChange('ssn_last_four', e.target.value.replace(/\D/g, '').slice(0, 4))}
          placeholder="1234"
        />
        <Typography level="body-xs" color="neutral">
          For verification purposes only
        </Typography>
      </FormControl>

      <Divider sx={{ my: 3 }} />

      {/* Address */}
      <Typography level="title-md" sx={{ mb: 2 }}>
        Home Address
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        <FormControl error={!!errors.address_line_1}>
          <FormLabel>Street Address *</FormLabel>
          <AddressAutocompleteInput
            value={formData.address_line_1}
            onChange={(value) => handleChange('address_line_1', value)}
            onAddressSelect={(parsed) => { setFormData(previous => ({ ...previous, address_line_1: parsed.address_1, city: parsed.city, state: parsed.state, zip_code: parsed.zip_code })); setErrors(previous => ({ ...previous, address_line_1: '', city: '', state: '', zip_code: '' })); }}
          />
          {errors.address_line_1 && (
            <Typography level="body-xs" color="danger">
              {errors.address_line_1}
            </Typography>
          )}
        </FormControl>
        <FormControl>
          <FormLabel>Address Line 2</FormLabel>
          <Input
            value={formData.address_line_2}
            onChange={(e) => handleChange('address_line_2', e.target.value)}
            placeholder="Apt, Suite, Unit, etc."
          />
        </FormControl>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' }, gap: 2 }}>
          <FormControl error={!!errors.city}>
            <FormLabel>City *</FormLabel>
            <Input
              value={formData.city}
              onChange={(e) => handleChange('city', e.target.value)}
              placeholder="City"
            />
            {errors.city && (
              <Typography level="body-xs" color="danger">
                {errors.city}
              </Typography>
            )}
          </FormControl>
          <FormControl error={!!errors.state}>
            <FormLabel>State *</FormLabel>
            <Select
              value={formData.state}
              onChange={(_, value) => handleChange('state', value as string)}
              placeholder="State"
            >
              {US_STATES.map((state) => (
                <Option key={state} value={state}>
                  {state}
                </Option>
              ))}
            </Select>
            {errors.state && (
              <Typography level="body-xs" color="danger">
                {errors.state}
              </Typography>
            )}
          </FormControl>
          <FormControl error={!!errors.zip_code}>
            <FormLabel>ZIP *</FormLabel>
            <Input
              value={formData.zip_code}
              onChange={(e) => handleChange('zip_code', e.target.value)}
              placeholder="12345"
            />
            {errors.zip_code && (
              <Typography level="body-xs" color="danger">
                {errors.zip_code}
              </Typography>
            )}
          </FormControl>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Emergency Contact */}
      <Typography level="title-md" sx={{ mb: 2 }}>
        Emergency Contact
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
        <FormControl error={!!errors.emergency_contact_name}>
          <FormLabel>Contact Name *</FormLabel>
          <Input
            value={formData.emergency_contact_name}
            onChange={(e) => handleChange('emergency_contact_name', e.target.value)}
            placeholder="Jane Doe"
          />
          {errors.emergency_contact_name && (
            <Typography level="body-xs" color="danger">
              {errors.emergency_contact_name}
            </Typography>
          )}
        </FormControl>
        <FormControl error={!!errors.emergency_contact_phone}>
          <FormLabel>Contact Phone *</FormLabel>
          <Input
            value={formData.emergency_contact_phone}
            onChange={(e) => handleChange('emergency_contact_phone', e.target.value)}
            placeholder="(555) 987-6543"
          />
          {errors.emergency_contact_phone && (
            <Typography level="body-xs" color="danger">
              {errors.emergency_contact_phone}
            </Typography>
          )}
        </FormControl>
        <FormControl>
          <FormLabel>Relationship</FormLabel>
          <Select
            value={formData.emergency_contact_relationship}
            onChange={(_, value) => handleChange('emergency_contact_relationship', value as string)}
            placeholder="Select"
          >
            <Option value="spouse">Spouse</Option>
            <Option value="parent">Parent</Option>
            <Option value="sibling">Sibling</Option>
            <Option value="child">Child</Option>
            <Option value="friend">Friend</Option>
            <Option value="other">Other</Option>
          </Select>
        </FormControl>
      </Box>

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button variant="outlined" color="neutral" onClick={onBack} disabled={isFirstStep}>
          Back
        </Button>
        <Button onClick={handleSubmit}>Continue</Button>
      </Box>
    </Box>
  );
};

export default PersonalDetailsStep;
